import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  OnboardingStep,
  OnboardingStepId,
  OnboardingProgress,
  OnboardingStatusResponse,
} from '../types/onboarding'
import { useAuth } from './useAuth'
import { onboardingService } from '../services/onboardingService'

/**
 * Custom hook for managing therapist onboarding state
 * Tracks completion status, persists to localStorage, and provides helper methods
 */
export const useOnboarding = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [apiStatus, setApiStatus] = useState<OnboardingStatusResponse | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(true)

  // Default onboarding steps (only 4 mandatory steps)
  const defaultSteps: OnboardingStep[] = [
    {
      id: OnboardingStepId.SUBSCRIPTION_PLAN,
      title: 'Choose Subscription',
      description: 'Select a plan that fits your practice needs',
      icon: 'CardMembership',
      actionText: 'Select Plan',
      actionPath: '/subscription',
      completed: false,
      required: true,
      order: 1,
      color: '#f59e0b',
    },
    {
      id: OnboardingStepId.CONNECT_BANK,
      title: 'Connect Bank Account',
      description: 'Link Stripe to receive payments securely and efficiently',
      icon: 'AccountBalance',
      actionText: 'Connect Stripe',
      actionPath: '/settings/payments',
      completed: false,
      required: true,
      order: 2,
      color: '#10b981',
    },
    {
      id: OnboardingStepId.SET_AVAILABILITY,
      title: 'Set Availability',
      description: 'Configure your schedule for client bookings',
      icon: 'CalendarMonth',
      actionText: 'Set Schedule',
      actionPath: '/account-settings?tab=schedule',
      completed: false,
      required: true,
      order: 3,
      color: '#8b5cf6',
    },
    {
      id: OnboardingStepId.ADD_SERVICES,
      title: 'Add Service Packages',
      description: 'Define your therapy services, pricing, and details',
      icon: 'LocalOffer',
      actionText: 'Add Services',
      actionPath: '/service-packages/new',
      completed: false,
      required: true,
      order: 4,
      color: '#3b82f6',
    },
  ]

  // Fetch onboarding status from API on mount
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      try {
        setIsLoading(true)
        const status = await onboardingService.getOnboardingStatus()
        setApiStatus(status)
      } catch (error) {
        console.error('Error fetching onboarding status:', error)
        setApiStatus({
          isConnectBank: false,
          isSubscriptionTaken: false,
          isServiceCreated: false,
          isSlotAdded: false,
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchOnboardingStatus()
  }, [])

  // Helper function to map step ID to API field (defined before useMemo)
  const getStepCompletionStatus = useCallback(
    (stepId: OnboardingStepId, status: OnboardingStatusResponse): boolean => {
      const mapping: Partial<
        Record<OnboardingStepId, keyof OnboardingStatusResponse>
      > = {
        [OnboardingStepId.CONNECT_BANK]: 'isConnectBank',
        [OnboardingStepId.SUBSCRIPTION_PLAN]: 'isSubscriptionTaken',
        [OnboardingStepId.SET_AVAILABILITY]: 'isSlotAdded',
        [OnboardingStepId.ADD_SERVICES]: 'isServiceCreated',
      }
      const fieldName = mapping[stepId]
      return fieldName ? status[fieldName] || false : false
    },
    []
  )

  // Map API status to steps
  const steps = useMemo<OnboardingStep[]>(() => {
    if (!apiStatus) return defaultSteps

    return defaultSteps.map(step => ({
      ...step,
      completed: getStepCompletionStatus(step.id, apiStatus),
    }))
  }, [apiStatus, getStepCompletionStatus])

  // Calculate progress metrics
  const progress = useMemo<OnboardingProgress>(() => {
    const completedCount = steps.filter(step => step.completed).length
    const totalCount = steps.length
    const completionPercentage = Math.round((completedCount / totalCount) * 100)
    const isOnboardingComplete = steps
      .filter(step => step.required)
      .every(step => step.completed)

    return {
      userId: user?.id || '',
      steps,
      completedCount,
      totalCount,
      completionPercentage,
      lastUpdated: new Date().toISOString(),
      isOnboardingComplete,
    }
  }, [steps, user?.id])

  // No longer persisting to localStorage - using API instead

  // Mark a step as complete (API call)
  const markStepComplete = useCallback(
    async (stepId: OnboardingStepId) => {
      if (!apiStatus) return

      const mapping: Partial<
        Record<OnboardingStepId, keyof OnboardingStatusResponse>
      > = {
        [OnboardingStepId.CONNECT_BANK]: 'isConnectBank',
        [OnboardingStepId.SUBSCRIPTION_PLAN]: 'isSubscriptionTaken',
        [OnboardingStepId.SET_AVAILABILITY]: 'isSlotAdded',
        [OnboardingStepId.ADD_SERVICES]: 'isServiceCreated',
      }

      try {
        const fieldName = mapping[stepId]
        if (!fieldName) return

        const updatedStatus = await onboardingService.updateOnboardingStep(
          fieldName,
          true
        )
        setApiStatus(updatedStatus)
      } catch (error) {
        console.error('Error updating onboarding step:', error)
      }
    },
    [apiStatus]
  )

  // Mark a step as incomplete (API call)
  const markStepIncomplete = useCallback(
    async (stepId: OnboardingStepId) => {
      if (!apiStatus) return

      const mapping: Partial<
        Record<OnboardingStepId, keyof OnboardingStatusResponse>
      > = {
        [OnboardingStepId.CONNECT_BANK]: 'isConnectBank',
        [OnboardingStepId.SUBSCRIPTION_PLAN]: 'isSubscriptionTaken',
        [OnboardingStepId.SET_AVAILABILITY]: 'isSlotAdded',
        [OnboardingStepId.ADD_SERVICES]: 'isServiceCreated',
      }

      try {
        const fieldName = mapping[stepId]
        if (!fieldName) return

        const updatedStatus = await onboardingService.updateOnboardingStep(
          fieldName,
          false
        )
        setApiStatus(updatedStatus)
      } catch (error) {
        console.error('Error updating onboarding step:', error)
      }
    },
    [apiStatus]
  )

  // Toggle step completion (API call)
  const toggleStepCompletion = useCallback(
    async (stepId: OnboardingStepId) => {
      const step = steps.find(s => s.id === stepId)
      if (!step) return

      if (step.completed) {
        await markStepIncomplete(stepId)
      } else {
        await markStepComplete(stepId)
      }
    },
    [steps, markStepComplete, markStepIncomplete]
  )

  // Navigate to step action (handles special cases)
  const navigateToStep = useCallback(
    (stepId: OnboardingStepId) => {
      const step = steps.find(s => s.id === stepId)
      if (!step) return

      // Handle special navigation cases
      if (stepId === OnboardingStepId.SET_AVAILABILITY) {
        // Navigate to account settings with schedule tab
        navigate('/account-settings?tab=schedule', { state: { from: 'dashboard' } })
      } else {
        navigate(step.actionPath)
      }
    },
    [steps, navigate]
  )

  // Reset all progress (for demo purposes)
  const resetProgress = useCallback(async () => {
    try {
      // Reset all steps to incomplete
      setApiStatus({
        isConnectBank: false,
        isSubscriptionTaken: false,
        isServiceCreated: false,
        isSlotAdded: false,
      })
    } catch (error) {
      console.error('Error resetting progress:', error)
    }
  }, [])

  // Get incomplete steps only
  const incompleteSteps = useMemo(
    () =>
      steps.filter(step => !step.completed).sort((a, b) => a.order - b.order),
    [steps]
  )

  // Get completed steps
  const completedSteps = useMemo(
    () =>
      steps.filter(step => step.completed).sort((a, b) => a.order - b.order),
    [steps]
  )

  // Get required incomplete steps
  const requiredIncompleteSteps = useMemo(
    () =>
      steps
        .filter(step => step.required && !step.completed)
        .sort((a, b) => a.order - b.order),
    [steps]
  )

  return {
    steps,
    progress,
    incompleteSteps,
    completedSteps,
    requiredIncompleteSteps,
    markStepComplete,
    markStepIncomplete,
    toggleStepCompletion,
    navigateToStep,
    resetProgress,
    isOnboardingComplete: progress.isOnboardingComplete,
    isLoading,
    apiStatus,
  }
}

export default useOnboarding
